import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import { FormattedMessage } from 'react-intl'
import SeparatedList from 'components/shared/SeparatedList'
import {
    useAddShippingToCartPutMutation,
    useCreateSelfcareOrderMutation,
} from 'store/api-slice/hooks'
import { formatPrice } from 'helpers/helpers'
import { handleError } from 'helpers/errors'
import { useApp } from 'store/steps'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { FieldError, setErrors } from 'store/order-slice'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import Link from 'components/shared/Link'
import { OPERATING_FEE, VOUCHER_DELIVERY_PRICE } from 'constants/prices'
import { setIsErrorModalOpened } from 'store/general-slice'
import { scrollTo } from 'helpers/animateScroll'
import { SUNTAGO_ONLINE_TICKET_SALES_REGULATIONS_LINK_CHANGES_PAGE } from 'constants/links'
import PaymentExpiryInfo from 'routes/OrderAndPayment/sections/PlaceOrderSection/components/PaymentExpiryInfo'
import { useSelfcareCart } from 'store/selfcare-cart-slice/hooks'
import { useVouchersContent } from 'utils/hooks/useVouchersContent'
import OrderSectionRow from './components/OrderSectionRow'
import { useMemo } from 'react'
import DiscountedProducts from 'components/global/omnibus/DiscountedProducts/index'
import { useDiscountedProducts } from 'utils/hooks/useDiscountedProducts'
import { SelfcarePlaceOrderSectionWithVouchers } from 'routes/selfcare/views/OrderAndPayment/SelfcarePlaceOrderSectionWithVouchers'

const SelfcarePlaceOrderSection = () => {
    const dispatch = useAppDispatch()
    const { language } = useApp()
    const { setErrorMessages } = useErrorMessages()
    const { hasDiscountedProducts } = useDiscountedProducts()
    const { isSelfcareCartFetching } = useSelfcareCart()
    const vouchersContent = useVouchersContent({})

    const {
        isAgreementChecked,
        optionalAgreements,
        shipping: shippingData,
    } = useAppSelector((state) => state.order)
    const {
        totalPrice,
        items,
        cartId,
        giftCards,
        newItemsPrice,
        refundPrice,
        payByCardPrice,
        remainingCardValue,
    } = useAppSelector((state) => state.selfcareCart)
    const {
        apartment,
        city,
        name,
        postCode,
        signature,
        street,
        surname,
        deliveryType,
        email,
        phone,
    } = shippingData

    const { handlingFee, shipping } = items

    const [placeSelfcareOrder, placeSelfcareOrderStatus] =
        useCreateSelfcareOrderMutation()

    const [addShippingToCart, addShippingToCartStatus] =
        useAddShippingToCartPutMutation()

    const nonNullableTotalPrice = totalPrice || 0
    const orderSumPrice = (totalPrice ?? 0) - (payByCardPrice ?? 0)

    const isShippingInCart = !!shipping
    const shippingPrice = isShippingInCart ? VOUCHER_DELIVERY_PRICE : 0

    const handlingFeesPrice = useMemo(() => {
        const handlingFees =
            handlingFee?.map((fee) => fee.totalPrice || 0) || []
        return handlingFees.reduce((prev, next) => prev + next, 0)
    }, [handlingFee])
    const isHandlingFeeInCart = handlingFeesPrice > 0

    const newOrChangedItemsPrice =
        nonNullableTotalPrice - handlingFeesPrice - shippingPrice
    const areNewItemsInCart = !!newItemsPrice

    const isShippingVisible =
        isShippingInCart && (areNewItemsInCart || isHandlingFeeInCart)
    const areNewOrChangedItemsVisible =
        areNewItemsInCart && (isShippingInCart || isHandlingFeeInCart)
    const isHandlingFeeVisible =
        isHandlingFeeInCart && (isShippingInCart || areNewItemsInCart)
    const areVouchersVisible = !!giftCards?.length

    const isPriceSplitted =
        isShippingVisible ||
        areNewOrChangedItemsVisible ||
        isHandlingFeeVisible ||
        areVouchersVisible

    const isTransactionWithRefund = !!refundPrice
    const isPostSelfcareOrderProcessing =
        addShippingToCartStatus.isLoading || placeSelfcareOrderStatus.isLoading

    const isOnlinePaymentRequired = !!totalPrice && orderSumPrice > 0

    const handlePostPlaceOrder = () => {
        if (!cartId) return

        placeSelfcareOrder({
            createSelfcareOrderCommand: {
                optionalAgreements,
                voucherSignature: isTransactionWithRefund ? signature : null,
            },
            _locale: language,
        })
            .then((data) => {
                handleError(data)

                if ('data' in data) {
                    const { link } = data.data
                    if (!link) return

                    window.location.href = link
                }
            })
            .catch((error) => {
                setErrorMessages(error)

                if (`${error.status}`[0] === '5') {
                    dispatch(setIsErrorModalOpened(true))
                }
            })
    }

    const handlePostPlaceOrderWithShipment = () => {
        if (!cartId) return

        const shippingData =
            deliveryType === 'email'
                ? {
                      email,
                      name: null,
                      surname: null,
                      street: null,
                      postCode: null,
                      city: null,
                      phone: null,
                      deliveryType,
                      shippingMethodCode: 'email',
                  }
                : {
                      email: null,
                      apartment: apartment.trim() || null,
                      city,
                      deliveryType,
                      name,
                      postCode,
                      street,
                      surname,
                      phone,
                      shippingMethodCode: 'inpost_delivery_address',
                  }

        addShippingToCart({
            _locale: language,
            addShippingToCartCommand: {
                shippingData,
                cartId,
            },
        })
            .then((data) => {
                handleError(data)

                if ('data' in data) {
                    handlePostPlaceOrder()
                }
            })
            .catch((error) => {
                setErrorMessages(error)

                if (`${error.status}`[0] === '5') {
                    dispatch(setIsErrorModalOpened(true))
                }
            })
    }

    const handlePlaceOrder = () => {
        dispatch(setErrors([]))

        if (!cartId) return
        if (!isAgreementChecked) {
            const message: FieldError[] = [
                {
                    property_path: 'isRequitedTermsAccepted',
                    message: 'This value should not be blank.',
                },
            ]

            scrollTo({
                id: 'agreementsBox',
                duration: 1000,
                margin: 100,
            })

            return dispatch(setErrors(message))
        }

        isTransactionWithRefund
            ? handlePostPlaceOrderWithShipment()
            : handlePostPlaceOrder()
    }

    return (
        <Section hasSmallerSpacingsY>
            <Box mb={32}>{vouchersContent?.vouchers}</Box>
            <SeparatedList
                gap={16}
                borderWidth={2}
                borderColor="grayscale_200"
                direction="column"
                hasLastItemBorder={false}
                turnOffChildTopBorder={[1]}
            >
                {isPriceSplitted && (
                    <Box isFullWidth gap={8}>
                        {isShippingVisible && (
                            <OrderSectionRow
                                price={VOUCHER_DELIVERY_PRICE}
                                title={
                                    <FormattedMessage
                                        id="selfcare.order.voucher_price"
                                        defaultMessage="Wysyłka vouchera kurierem"
                                    />
                                }
                            />
                        )}

                        {areNewOrChangedItemsVisible && (
                            <OrderSectionRow
                                price={newOrChangedItemsPrice}
                                title={
                                    <FormattedMessage
                                        id="selfcare.order.price_for_added_items"
                                        defaultMessage="Dodane lub zmienione usługi"
                                    />
                                }
                            />
                        )}

                        {isHandlingFeeVisible && (
                            <Box isFullWidth>
                                <OrderSectionRow
                                    price={handlingFeesPrice}
                                    title={
                                        <FormattedMessage
                                            id="selfcare.order.price_for_changes"
                                            defaultMessage="Opłata za zmianę"
                                        />
                                    }
                                    hasDisclaimer
                                />
                                <Text variant="bodySmall" color="grayscale_800">
                                    *
                                    <FormattedMessage
                                        id="selfcare.order.price_for_changes_clarification"
                                        defaultMessage="Każda zmiana w zamówieniu wiąże się z opłatą operacyjną w wysokości {price}."
                                        values={{
                                            price: formatPrice(
                                                OPERATING_FEE,
                                                true
                                            ),
                                        }}
                                    />
                                    <Link
                                        variant="secondary"
                                        icon={null}
                                        target="_blank"
                                        path={
                                            SUNTAGO_ONLINE_TICKET_SALES_REGULATIONS_LINK_CHANGES_PAGE
                                        }
                                        data-testid="payment-redirect-fee-regulations"
                                    >
                                        <Text
                                            variant="bodySmall"
                                            ml={4}
                                            as="span"
                                        >
                                            <FormattedMessage
                                                id="global.regulations"
                                                defaultMessage="Regulamin"
                                            />
                                        </Text>
                                    </Link>
                                </Text>
                            </Box>
                        )}

                        {areVouchersVisible && (
                            <SelfcarePlaceOrderSectionWithVouchers
                                isSelfcareCartFetching={isSelfcareCartFetching}
                                payByCardPrice={payByCardPrice}
                                remainingCardValue={remainingCardValue}
                            />
                        )}
                    </Box>
                )}

                <OrderSectionRow
                    price={orderSumPrice}
                    title={
                        <FormattedMessage
                            id="global.price.label_total_summary"
                            defaultMessage="Do zapłaty"
                        />
                    }
                    hasHighlightedPrice
                />
            </SeparatedList>

            {hasDiscountedProducts && (
                <DiscountedProducts
                    mt={[32, 40]}
                    mb={[32, 40]}
                    hasAdditionalExplanation
                />
            )}

            <Box align="flex-end" mt={isPriceSplitted ? 32 : 16}>
                <Button
                    onClick={handlePlaceOrder}
                    isLoading={isPostSelfcareOrderProcessing}
                    width={['100%', '180px']}
                    hasNoWrap={true}
                    testId="payment-button-pay"
                >
                    <FormattedMessage
                        id="order.discount.btn_payment_caption"
                        defaultMessage="Kupuję i płacę"
                    />
                </Button>
                {isOnlinePaymentRequired && (
                    <PaymentExpiryInfo
                        isCartFetching={isSelfcareCartFetching}
                    />
                )}
            </Box>
        </Section>
    )
}

export default SelfcarePlaceOrderSection
